import { Box, Button, CircularProgress, Grid, MenuItem, TextField } from "@mui/material";
import { fynGstDetails } from "api/services/users";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { snack } from "components/toast";
import { Dispatch, SetStateAction, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { handleChange } from "redux/reducers/billingEntitySlice";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { createBillingEntity, getBillingEntity } from "api/services/billingEntity";
import { ResType } from "types";
import { DesktopDatePicker } from "@mui/lab";
import moment from "moment";
import { handleKeyDown } from "utils/keyDownPrevent";
import { client_categories } from "views/signup/PanDetails";
import SafeTextField from "components/SafeTextField";

interface Props {
    setOpen: Dispatch<SetStateAction<boolean>>;
    onSuccess?: () => void;
}

const GstDetails = ({ setOpen, onSuccess }: Props) => {
    const [state, setState]: any = useState({ showDiscount: 'true' });
    const [gstNumber, setGstNumber] = useState("");
    const [isVerified, setIsVerified] = useState(false);
    const [gstLoading, setGstLoading] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [forFocus, setForFocus] = useState(true);
    const navigate = useNavigate();

    const { data, isLoading: loading }: ResType = useQuery(["billing-entities"], getBillingEntity, {
        onError: (err: any) => {
            if (!err?.error) {
                snack.error(`An error occurred while fetching billing entities data`);
            }
        }
    });

    const GstAdornment = () => {
        const showGstActive = isVerified;
        const showGstVerify = !isVerified;

        return (
            <>
                {gstLoading && <CircularProgress size="1rem" />}
                {showGstActive && !gstLoading && (
                    <CheckCircleIcon fontSize="small" sx={{ color: "green" }} />
                )}
                {showGstVerify && !gstLoading && (
                    <Button color="error" disabled={gstLoading} size="small" onClick={verifyGst}>
                        Verify
                    </Button>
                )}
            </>
        );
    };
    const gstinPattern = /^[0-9]{2}[A-Z]{3}[A,B,C,F,G,H,L,J,P,T]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}[1-9A-Za-z]{1}[Z]{1}[0-9A-Z]{1}$/;

    const verifyGst = async () => {
        if (!gstNumber) return snack.error("Enter GST Number");
        if (gstNumber.length !== 15 || !gstinPattern.test(gstNumber)) {
            return snack.error("Invalid GSTIN Pattern");
        }

        setGstLoading(true);
        try {

            let response: any = await fynGstDetails({ gstNumber, be: true });


            if (response?.data?.data?.sts !== "Active") {
                const status = response?.data?.data?.sts
                return snack.error(`${status} GST! Please enter a Valid GSTIN`);
            }

            const result: any = response.data;
            setIsVerified(true)
            setState({
                legalName: result?.data?.lgnm,
                tradeName: result?.data?.tradeNam,
                gstNumber: result?.data?.gstin,
                panNumber: result?.data?.gstin.slice(2, -3),
                placeOfSupply: result?.data?.pradr?.addr?.stcd,
                registrationDate: result?.data?.rgdt,
                gstStatus: result?.data?.sts,
                buildingNumber: result?.data?.pradr?.addr?.bno,
                floorNumber: result?.data?.pradr?.addr?.flno,
                buildingName: result?.data?.pradr?.addr?.bnm,
                street: result?.data?.pradr?.addr?.st,
                location: result?.data?.pradr?.addr?.locality,
                city: result?.data?.pradr?.addr?.loc,
                district: result?.data?.pradr?.addr?.dst,
                state: result?.data?.pradr?.addr?.stcd,
                pincode: result?.data?.pradr?.addr?.pncd,
            });
        } catch (e: any) {
            if (e.response?.data?.code === 422) {
                snack.error("Invalid GSTIN");
            } else {
                snack.error(e.response?.data?.message);
            }
        } finally {
            setGstLoading(false);
        }
    };

    const handleGstChange = (e: any) => {
        setGstNumber(e.target.value.toUpperCase());
        setIsVerified(false);
    };

    const { mutate, isLoading } = useMutation(createBillingEntity, {
        onSuccess: (res) => {
            snack.success("Billing Entity Created");
            if (onSuccess) {
                onSuccess();
            } else {
                navigate(`/settings/billing-entities/${res?.data?.id}`);
            }
            setOpen(false);
        },

        onError: (err: any) => {
            snack.error(err.response.data.message);
        },
    });

    const handleClick = () => {
        const duplicateTradeName = data.data.filter(
            (item) =>
                item.tradeName?.trim().toLowerCase() ===
                state.tradeName?.trim().toLowerCase()
        );

        if (duplicateTradeName.length) {
            return snack.error("Trade Name Already Exists");
        }
        if (gstNumber !== undefined && (gstNumber.length !== 15 || !gstinPattern.test(gstNumber))) {
            return snack.error("Invalid GST Pattern");
        }
        if (!state.tradeName) return snack.error('Trade Name is required');
        if (!state.category) return snack.error('Constitution of Business is Required !');
        if (!state.registrationDate) return snack.error('Effective Date Of Registration is Required !');
        mutate({ ...state, hasGst: true, panNumber: gstNumber.slice(2, -3), gstNumber: gstNumber, gstVerified: isVerified ? true : false, panVerified: true });
    };
    const handleSelect = () => {
        setIsDatePickerOpen(false);
        setForFocus(false);
    };

    const handleFocus = () => {
        if (forFocus)
            setIsDatePickerOpen(true);
        setForFocus(true);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.name === 'panNumber') {
            const no = event.target.value.toUpperCase()
            setState(prevState => ({
                ...prevState,
                [event.target.name]: no
            }));
        } else {
            setState({ ...state, [event.target.name]: event.target.value });

        }
    };

    const isValidGst = (gst) => {
        return gstinPattern.test(gst);
    };

    return (
        <>
            <Box>
                <TextField
                    required
                    onChange={handleGstChange}
                    value={gstNumber}
                    sx={{ mt: 2 }}
                    label="GSTIN"
                    name="gstNumber"
                    size="small"
                    fullWidth
                    onKeyDown={(e: any) => {
                        if (e.keyCode === 13 && !gstLoading) {
                            verifyGst();
                        }
                    }}
                    InputProps={{
                        endAdornment: <GstAdornment />,
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        pattern: "^[0-9]{2}[A-Z]{3}[A,B,C,F,G,H,L,J,P,T]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}[1-9A-Za-z]{1}[Z]{1}[0-9A-Z]{1}$",
                        title: "GSTIN is invalid",
                        maxlength: 15
                    }}
                    error={!isValidGst(gstNumber) && gstNumber !== "" && gstNumber !== null && gstNumber !== undefined}
                    helperText={(!isValidGst(gstNumber) && gstNumber !== "" && gstNumber !== null && gstNumber !== undefined) && 'Invalid GSTIN'}
                />
            </Box>
            <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={12}>
                    <TextField
                        sx={{ mt: 2 }}
                        variant="outlined"
                        size="small"
                        fullWidth
                        select
                        onChange={handleChange}
                        value={state.category ?? ""}
                        InputLabelProps={{ shrink: true }}
                        required
                        name="category"
                        label="Constitution of business"
                    >
                        {client_categories.map((item, index) => (
                            <MenuItem key={index} value={item.value}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SafeTextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={state.legalName}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        name="legalName"
                        label="Legal Name of The Business"
                    />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SafeTextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={state.tradeName}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                        name="tradeName"
                        label="Trade Name of The Business"
                    />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <DesktopDatePicker
                        label="Effective Date Of Registration"
                        inputFormat="dd-MM-yyyy"
                        value={state?.registrationDate ? moment(state?.registrationDate, 'DD/MM/YYYY').toDate() : null}
                        maxDate={moment().toDate()}
                        onChange={(value) => {
                            setState({
                                ...state,
                                registrationDate: moment(value).format("DD/MM/YYYY"),
                            });
                            handleSelect();
                        }}
                        open={isDatePickerOpen}
                        renderInput={(params) => <TextField
                            sx={{ mt: 2 }}
                            required
                            fullWidth
                            size="small"
                            {...params}
                            onKeyDown={handleKeyDown}
                            onClick={() => { setIsDatePickerOpen(true); }}
                            onFocus={handleFocus}
                        />}
                    />

                </Grid>
                <Grid item xs={12} sm={12}>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        select
                        value={state.showDiscount}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                        name="showDiscount"
                        label="Need Discount Column"

                    >

                        <MenuItem key={1} value={'true'}>
                            Yes
                        </MenuItem>
                        <MenuItem key={2} value={'false'}>
                            No
                        </MenuItem>

                    </TextField>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Button type="submit" variant="contained" disabled={false} fullWidth color="error" onClick={handleClick}>
                        Create Entity
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};
export default GstDetails;